import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  FILTER_ALL_VALUE,
  PAGE_SIZE_OPTIONS,
} from "constants/constants";
import routes from "constants/routes";
import {
  ACTIVE_COUPONS_DEFAULT_LIMIT,
  COUPON_STATUS,
  COUPON_CREATED_ON_SORT_OPTIONS,
  COUPON_LIST_CATEGORY_FILTER_OPTIONS,
  COUPON_LIST_STATUS_FILTER_OPTIONS,
  COUPON_SORT_DIRECTION,
} from "constants/coupons";
import {
  formatCouponCategory,
  formatCouponDate,
  formatCouponDateRange,
  formatCouponDiscount,
  formatUsageLimit,
  getCouponStatusLabel,
  resolveCouponStatus,
} from "utils/couponUtils";
import { formatApiErrorForDisplay } from "helpers/packageManagementApi";
import {
  fetchActiveCoupons,
  useActiveCouponsList,
  useCouponInfo,
} from "store/backend/apiHooks";
import Loader from "shared/Loader/Loader";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./CouponsList.module.scss";

const STATUS_CLASS_MAP = {
  [COUPON_STATUS.ACTIVE]: styles.statusActive,
  [COUPON_STATUS.INACTIVE]: styles.statusInactive,
  [COUPON_STATUS.EXPIRED]: styles.statusExpired,
  [COUPON_STATUS.SCHEDULED]: styles.statusScheduled,
};

const getSearchErrorMessage = (error) =>
  formatApiErrorForDisplay(error, "Failed to fetch coupon info.");

const FilterSelect = ({
  label,
  icon: Icon,
  iconClassName = "",
  value,
  onChange,
  options,
  ariaLabel,
}) => (
  <div className={styles.filterField}>
    <span className={styles.filterLabel}>{label}</span>
    <div className={styles.filterSelectWrap}>
      {Icon && (
        <Icon
          className={`${styles.filterFieldIcon} ${iconClassName}`}
          fontSize="small"
        />
      )}
      <select
        className={styles.filterSelect}
        value={value}
        onChange={onChange}
        aria-label={ariaLabel || label}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <UnfoldMoreOutlinedIcon className={styles.filterChevron} fontSize="small" />
    </div>
  </div>
);

const CouponsList = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [searchInput, setSearchInput] = useState("");
  const [couponLookupCode, setCouponLookupCode] = useState("");
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [categoryFilter, setCategoryFilter] = useState(FILTER_ALL_VALUE);
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL_VALUE);
  const [createdOnSort, setCreatedOnSort] = useState(COUPON_SORT_DIRECTION.DESC);
  const [codeSort, setCodeSort] = useState(null);
  const [extraCoupons, setExtraCoupons] = useState([]);
  const [hasMoreCoupons, setHasMoreCoupons] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const searchQuery = searchInput.trim().toLowerCase();

  const {
    data: activeCouponsData,
    isLoading,
    isError,
    error,
    isFetching,
  } = useActiveCouponsList();

  useEffect(() => {
    if (!activeCouponsData?.coupons) return;

    setHasMoreCoupons(
      activeCouponsData.coupons.length >= ACTIVE_COUPONS_DEFAULT_LIMIT
    );
  }, [activeCouponsData?.coupons]);

  const loadedCoupons = useMemo(
    () => [...(activeCouponsData?.coupons ?? []), ...extraCoupons],
    [activeCouponsData, extraCoupons]
  );

  const loadNextBatch = async () => {
    if (isLoadingMore || !hasMoreCoupons) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchActiveCoupons({
        offset: loadedCoupons.length,
        limit: ACTIVE_COUPONS_DEFAULT_LIMIT,
      });
      setExtraCoupons((prev) => [...prev, ...result.coupons]);
      setHasMoreCoupons(
        result.coupons.length >= ACTIVE_COUPONS_DEFAULT_LIMIT
      );
    } catch (loadError) {
      openSnackbar(
        formatApiErrorForDisplay(loadError, "Failed to load more coupons."),
        "error"
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  const {
    data: lookedUpCoupon,
    isFetching: isLookingUpCoupon,
    isError: isCouponLookupError,
    error: couponLookupError,
    isSuccess: isCouponLookupSuccess,
  } = useCouponInfo(couponLookupCode);

  useEffect(() => {
    if (!couponLookupCode || !isCouponLookupSuccess || !lookedUpCoupon) return;

    const couponCode =
      lookedUpCoupon?.couponCode || lookedUpCoupon?.code || couponLookupCode;
    navigate(routes.coupons.view(couponCode));
    setCouponLookupCode("");
  }, [
    couponLookupCode,
    isCouponLookupSuccess,
    lookedUpCoupon,
    navigate,
  ]);

  useEffect(() => {
    if (!couponLookupCode || !isCouponLookupError) return;

    openSnackbar(getSearchErrorMessage(couponLookupError), "error");
    setCouponLookupCode("");
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  }, [
    couponLookupCode,
    isCouponLookupError,
    couponLookupError,
    openSnackbar,
  ]);

  const displayCoupons = useMemo(() => {
    let coupons = [...loadedCoupons];

    if (searchQuery) {
      coupons = coupons.filter((coupon) =>
        coupon.code.toLowerCase().includes(searchQuery)
      );
    }

    if (categoryFilter !== FILTER_ALL_VALUE) {
      coupons = coupons.filter((coupon) => coupon.category === categoryFilter);
    }

    if (statusFilter !== FILTER_ALL_VALUE) {
      coupons = coupons.filter((coupon) => {
        const couponStatus = resolveCouponStatus(
          coupon.effectiveDate,
          coupon.expiryDate
        );
        return couponStatus === statusFilter;
      });
    }

    coupons.sort((a, b) => {
      let comparison = 0;

      if (codeSort) {
        comparison = (a.code || "")
          .toLowerCase()
          .localeCompare((b.code || "").toLowerCase());
        return codeSort === COUPON_SORT_DIRECTION.ASC ? comparison : -comparison;
      }

      const dateA = new Date(a.createdOn || 0).getTime();
      const dateB = new Date(b.createdOn || 0).getTime();

      if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
      if (Number.isNaN(dateA)) return 1;
      if (Number.isNaN(dateB)) return -1;

      comparison = dateA - dateB;
      return createdOnSort === COUPON_SORT_DIRECTION.ASC ? comparison : -comparison;
    });

    return coupons;
  }, [
    loadedCoupons,
    searchQuery,
    categoryFilter,
    statusFilter,
    createdOnSort,
    codeSort,
  ]);

  const totalLoadedPages = Math.max(
    1,
    Math.ceil(displayCoupons.length / pageSize)
  );

  const paginatedCoupons = displayCoupons.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const hasNextPage =
    currentPage < totalLoadedPages || Boolean(hasMoreCoupons);

  const startItem =
    paginatedCoupons.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const endItem = Math.min(currentPage * pageSize, displayCoupons.length);

  const totalLabel = hasMoreCoupons
    ? `${Math.max(
        ACTIVE_COUPONS_DEFAULT_LIMIT,
        Math.ceil(endItem / ACTIVE_COUPONS_DEFAULT_LIMIT) *
          ACTIVE_COUPONS_DEFAULT_LIMIT
      )}+`
    : displayCoupons.length;

  const hasActiveFilters =
    Boolean(searchInput.trim()) ||
    categoryFilter !== FILTER_ALL_VALUE ||
    statusFilter !== FILTER_ALL_VALUE ||
    createdOnSort !== COUPON_SORT_DIRECTION.DESC ||
    codeSort !== null;

  const handleClearFilters = () => {
    setSearchInput("");
    setCategoryFilter(FILTER_ALL_VALUE);
    setStatusFilter(FILTER_ALL_VALUE);
    setCreatedOnSort(COUPON_SORT_DIRECTION.DESC);
    setCodeSort(null);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key !== "Enter") return;

    const query = searchInput.trim();
    if (!query) return;

    event.preventDefault();
    setCouponLookupCode(query);
  };

  const toggleCodeSort = () => {
    setCodeSort((current) => {
      if (!current) return COUPON_SORT_DIRECTION.ASC;
      return current === COUPON_SORT_DIRECTION.ASC
        ? COUPON_SORT_DIRECTION.DESC
        : COUPON_SORT_DIRECTION.ASC;
    });
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  return (
    <div className={styles.container}>
      <div className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchFilter}>
            <div className={styles.searchInputWrap}>
              <SearchOutlinedIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search by coupon code..."
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  setCurrentPage(DEFAULT_PAGE_NUMBER);
                }}
                onKeyDown={handleSearchKeyDown}
                disabled={isLookingUpCoupon}
              />
            </div>
          </div>

          <FilterSelect
            label="Category"
            icon={LocalOfferOutlinedIcon}
            iconClassName={styles.categoryFilterIcon}
            value={categoryFilter}
            onChange={(event) => {
              setCategoryFilter(event.target.value);
              setCurrentPage(DEFAULT_PAGE_NUMBER);
            }}
            options={COUPON_LIST_CATEGORY_FILTER_OPTIONS}
          />

          <FilterSelect
            label="Status"
            icon={FiberManualRecordIcon}
            iconClassName={styles.statusFilterIcon}
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setCurrentPage(DEFAULT_PAGE_NUMBER);
            }}
            options={COUPON_LIST_STATUS_FILTER_OPTIONS}
          />

          <FilterSelect
            label="Created On"
            icon={CalendarTodayOutlinedIcon}
            iconClassName={styles.createdOnFilterIcon}
            value={createdOnSort}
            onChange={(event) => {
              setCreatedOnSort(event.target.value);
              setCodeSort(null);
              setCurrentPage(DEFAULT_PAGE_NUMBER);
            }}
            options={COUPON_CREATED_ON_SORT_OPTIONS}
          />

          <div className={styles.filterActions}>
            <button
              type="button"
              className={styles.createButton}
              onClick={() => navigate(routes.coupons.create)}
            >
              <AddIcon fontSize="small" />
              Create Coupon
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                className={styles.clearFiltersBtn}
                onClick={handleClearFilters}
                aria-label="Clear Filters"
              >
                <RestartAltOutlinedIcon fontSize="small" />
                <span className={styles.clearFiltersTooltip}>Clear Filters</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.emptyState}>
            <Loader />
          </div>
        ) : isError ? (
          <div className={styles.errorState} role="alert">
            {formatApiErrorForDisplay(error, "Failed to load active coupons.")}
          </div>
        ) : (
          <>
            <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <button
                      type="button"
                      className={styles.sortButton}
                      onClick={toggleCodeSort}
                      aria-label="Sort coupon code"
                    >
                      <span>Coupon Code</span>
                      {codeSort && (
                        <span className={styles.sortIcon}>
                          {codeSort === COUPON_SORT_DIRECTION.ASC ? (
                            <ArrowUpwardIcon fontSize="inherit" />
                          ) : (
                            <ArrowDownwardIcon fontSize="inherit" />
                          )}
                        </span>
                      )}
                    </button>
                  </th>
                  <th>Coupon Category</th>
                  <th>Discount Type</th>
                  <th>Discount</th>
                  <th>Validity</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th>Created On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCoupons.length === 0 ? (
                  <tr>
                    <td colSpan={9} className={styles.emptyState}>
                      No active coupons found.
                    </td>
                  </tr>
                ) : (
                  paginatedCoupons.map((coupon) => {
                    const couponStatus = resolveCouponStatus(
                      coupon.effectiveDate,
                      coupon.expiryDate
                    );

                    return (
                      <tr
                        key={coupon.id}
                        className={styles.clickableRow}
                        onClick={() => navigate(routes.coupons.view(coupon.id))}
                      >
                        <td className={styles.codeCell}>{coupon.code}</td>
                        <td>{formatCouponCategory(coupon.category)}</td>
                        <td style={{ textTransform: "capitalize" }}>
                          {coupon.discountType}
                        </td>
                        <td>{formatCouponDiscount(coupon)}</td>
                        <td>
                          {formatCouponDateRange(
                            coupon.effectiveDate,
                            coupon.expiryDate
                          )}
                        </td>
                        <td>{formatUsageLimit(coupon)}</td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${STATUS_CLASS_MAP[couponStatus]}`}
                          >
                            {getCouponStatusLabel(couponStatus)}
                          </span>
                        </td>
                        <td>{formatCouponDate(coupon.createdOn)}</td>
                        <td>
                          <div className={styles.actionsCell}>
                            <button
                              type="button"
                              className={styles.actionIconBtn}
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(routes.coupons.view(coupon.id));
                              }}
                              aria-label={`View ${coupon.code}`}
                              title="View"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </button>
                            <button
                              type="button"
                              className={styles.actionIconBtn}
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(routes.coupons.edit(coupon.id));
                              }}
                              aria-label={`Edit ${coupon.code}`}
                              title="Edit"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>

            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                {paginatedCoupons.length === 0
                  ? "No coupons to display"
                  : `Showing ${startItem} to ${endItem} of ${totalLabel} coupons`}
                {(isFetching || isLoadingMore) && !isLoading
                  ? " (updating...)"
                  : ""}
              </span>
              <div className={styles.pageSizeControl}>
                <label htmlFor="coupon-page-size" className={styles.pageSizeLabel}>
                  Rows per page
                </label>
                <select
                  id="coupon-page-size"
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setCurrentPage(DEFAULT_PAGE_NUMBER);
                  }}
                  disabled={isFetching || isLoadingMore}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.pageButton}
                  disabled={currentPage === 1 || isFetching || isLoadingMore}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className={`${styles.pageButton} ${styles.pageButtonActive}`}
                  disabled
                >
                  {currentPage}
                </button>
                <button
                  type="button"
                  className={styles.pageButton}
                  disabled={!hasNextPage || isFetching || isLoadingMore}
                  onClick={async () => {
                    if (currentPage < totalLoadedPages) {
                      setCurrentPage((page) => page + 1);
                      return;
                    }

                    if (hasMoreCoupons) {
                      await loadNextBatch();
                      setCurrentPage((page) => page + 1);
                    }
                  }}
                  aria-label="Next page"
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CouponsList;
